import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ComponentDataSourceService } from '../../component-data-source/component-data-source.service';
import { environment } from '../../../environment/environment';
import { DataSharingService } from '../../data-sharing/data-sharing.service';

export interface UpdateModuleRequest {
  module_id: number;
  name?: string;
  visibility?: string;
  maintenance_minutes?: number;
  descriptions?: Record<string, string>;
  subtypes?: Array<{ subtype_id: number; name?: string }>;
}

export interface UpdateModuleResponse {
  success: boolean;
  message: string;
  module_id?: number;
  version_id?: number;
  error?: string;
}

export function updateModule(componentDataSourceService: ComponentDataSourceService) {
  // Get the data sharing service from the component data source service
  const dataSharingService = componentDataSourceService.injector.get(DataSharingService);
  
  return {
    name: 'UpdateModule',
    description: 'Updates an existing module and its latest version with new data',
    execute: (data: UpdateModuleRequest): Observable<UpdateModuleResponse> => {
      
      if (!data.module_id) {
        console.error('[UpdateModule] No module_id provided');
        return of({
          success: false,
          message: 'No module_id provided',
          error: 'Missing required module_id'
        });
      }

      // Prepare the update payload
      const updatePayload: any = {};
      
      if (data.name !== undefined) updatePayload.name = data.name;
      if (data.visibility !== undefined) updatePayload.visibility = data.visibility;
      if (data.maintenance_minutes !== undefined) updatePayload.maintenance_minutes = data.maintenance_minutes;
      if (data.descriptions !== undefined) updatePayload.descriptions = data.descriptions;
      if (data.subtypes !== undefined) updatePayload.subtypes = data.subtypes;

      // Make PATCH request to update the module and its latest version
      return componentDataSourceService.http.patch<UpdateModuleResponse>(
        `${environment.apiUrl}/events/modules/${data.module_id}`,
        updatePayload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      ).pipe(
        tap(response => {
          
          // Trigger reactive updates for modules and module-versions
          if (response.success) {
            
            // Trigger modules list update
            dataSharingService.setData('modules_updated', { 
              timestamp: Date.now(),
              module_id: data.module_id 
            });
            
            // Update the selected module data if it's the same module being edited
            const selectedModule = dataSharingService.getData('selected_module');
            if (selectedModule && selectedModule.module_id === data.module_id) {
              dataSharingService.setData('selected_module', {
                ...selectedModule,
                ...updatePayload,
                // Update the latest version description if descriptions were updated
                ...(data.descriptions && {
                  latestVersionDescription: data.descriptions['da'] || data.descriptions['0'] || ''
                })
              });
            }
          }
        }),
        catchError(error => {
          console.error('[UpdateModule] HTTP error:', error);
          console.error('[UpdateModule] Error status:', error.status);
          console.error('[UpdateModule] Error message:', error.message);
          if (error.error) {
            console.error('[UpdateModule] Error body:', error.error);
          }
          throw error;
        })
      );
    }
  };
}
